import { useQuery } from '@tanstack/vue-query'
import fetchApi from '@/utils/fetchApi'
import type { Configurator, ConfiguratorPagination } from 'shared/src'
import { computed, type MaybeRef, toValue } from 'vue'

export interface ConfigurationsListParams {
  q?: string
  language?: string
  page?: number
  limit?: number
  locale?: string
}

export interface ConfigurationsListResult {
  configs: Configurator[]
  pagination: ConfiguratorPagination
}

export const configurationsListQueryKey = (params: ConfigurationsListParams) =>
  ['configurations', 'list', params] as const

export function useConfigurationsListQuery(params: MaybeRef<ConfigurationsListParams>) {
  const queryKey = computed(() => configurationsListQueryKey(toValue(params)))

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = toValue(params)
      const query: Record<string, string | number | boolean> = {}

      if (p.page) query.page = p.page
      if (p.limit) query.limit = p.limit
      if (p.q) query.q = p.q
      if (p.language) query.language = p.language
      if (p.locale) query.locale = p.locale

      const result = (await fetchApi<ConfigurationsListResult>({
        endpoint: '/api/configurations/list',
        query,
      })) as ConfigurationsListResult | null

      return result ?? defaultConfigurationsListResult()
    },
  })
}

function defaultConfigurationsListResult(): ConfigurationsListResult {
  return {
    configs: [],
    pagination: {
      has_more: false,
      limit: 20,
      page: 1,
      processing_time_ms: 0,
      total_hits: 0,
      total_pages: 0,
    },
  }
}
